/**
 * "New workflow" — the whole of what `/templates` used to be (DOP-478).
 *
 * Templates stopped being a place and became a choice: you are creating a
 * workflow, and the template is which KIND of workflow it is. So the picker
 * and the composer behind it live in a modal over the index rather than on a
 * page of their own — the list you are adding to stays behind it, and closing
 * puts you back where you were instead of on a different screen.
 *
 * Two steps. Step one picks the template; step two is the SAME composer the
 * Templates page hosted, unchanged, with a Back that returns to the picker.
 * Which template a visit starts on comes from the caller (`?template=`), so the
 * `/templates?compose=extract_kpis` redirect still lands on the KPI composer.
 *
 * Styled to the redesign (DOP-624 and the screens before it) through
 * `Workflows/composerStyles`, so the flow behind "+ New workflow" looks like the
 * Workflows table it adds to.
 *
 * Every input here focuses the same way — a neutral border plus a coloured
 * `focus:ring-1`, which is what the rest of the app already does. It matters
 * because `@tailwindcss/forms` is
 * registered with no options, i.e. the default `strategy: 'base'`: it restyles
 * every bare `input`/`textarea` in the base layer and paints its OWN blue ring
 * on focus. `focus:outline-none` does not remove that — the ring is a
 * box-shadow, not an outline — so a teal `focus:border-*` on top of it read as
 * two rings. Colouring the plugin's ring instead of fighting it leaves one.
 */

import { ArrowLeft, ClipboardList, FileSpreadsheet, FileText } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalDialog from "../ModalDialog";
import WorkflowComposer from "../Workflows/WorkflowComposer";
import WorkflowTemplateCard from "../Workflows/WorkflowTemplateCard";
import {
	BODY,
	BTN,
	BTN_PRIMARY,
	CARD,
	FIELD_LABEL,
	HINT,
	INPUT,
	LINK,
	PILL,
	TITLE,
} from "../Workflows/composerStyles";
import { useToast } from "../../context/ToastContext";
import { useKpiWorkflows } from "../../hooks/useKpiWorkflows";
import {
	DealScreeningRequestError,
	dealScreeningService,
} from "../../services/dealScreeningService";
import {
	MonitoringRequestError,
	monitoringService,
} from "../../services/monitoringService";
import type { CreateWorkflowPayload } from "../../types/kpiWorkflow";
import {
	friendlyApiErrorMessage,
	friendlyErrorMessage,
} from "../../utils/apiErrorDetail";
import {
	isDealScreeningEnabled,
	isKpiExtractionEnabled,
	isLoanTapeTemplateEnabled,
	isMonitoringEnabled,
} from "../../utils/featureFlags";

/** The `?template=` values, and the keys the picker's cards carry. */
export const NEW_DASHBOARD_TEMPLATES = [
	"extract_kpis",
	"loan_tape",
	"monitoring",
	"deal_screening",
] as const;

export type NewDashboardTemplate = (typeof NEW_DASHBOARD_TEMPLATES)[number];

export const isNewDashboardTemplate = (
	value: string | null,
): value is NewDashboardTemplate =>
	value !== null &&
	(NEW_DASHBOARD_TEMPLATES as readonly string[]).includes(value);

const TITLE_ID = "new-dashboard-title";

/** Where loan tape setup lives (DOP-627). */
export const NEW_LOAN_TAPE_PATH = "/workflows/loan-tape/new";

export interface NewDashboardModalProps {
	/**
	 * The template to open on, from `?template=`. `null` shows the picker, and
	 * so does a template this deployment has flagged off — a hidden template
	 * you can still bookmark your way into is not hidden.
	 */
	initialTemplate: NewDashboardTemplate | null;
	onClose: () => void;
	/** Called after a workflow is created, so the index can pick it up. */
	onCreated?: () => void;
}

const NewDashboardModal: React.FC<NewDashboardModalProps> = ({
	initialTemplate,
	onClose,
	onCreated,
}) => {
	const navigate = useNavigate();
	const { showToast } = useToast();
	const {
		metricGroups,
		folders,
		foldersLoading,
		foldersError,
		reloadFolders,
		isSaving,
		createWorkflow,
		runNow,
	} = useKpiWorkflows();

	const loanTapeEnabled = isLoanTapeTemplateEnabled();
	const monitoringEnabled = isMonitoringEnabled();
	const dealScreeningEnabled = isDealScreeningEnabled();
	const kpiExtractionEnabled = isKpiExtractionEnabled();
	const isAvailable = (key: NewDashboardTemplate | null): boolean =>
		(key === "extract_kpis" && kpiExtractionEnabled) ||
		(key === "loan_tape" && loanTapeEnabled) ||
		(key === "monitoring" && monitoringEnabled) ||
		(key === "deal_screening" && dealScreeningEnabled);

	const [template, setTemplate] = useState<NewDashboardTemplate | null>(() =>
		isAvailable(initialTemplate) ? initialTemplate : null,
	);
	const [describe, setDescribe] = useState("");
	const [monitoringName, setMonitoringName] = useState("");
	const [monitoringFund, setMonitoringFund] = useState("");
	const [monitoringSaving, setMonitoringSaving] = useState(false);
	const [screeningName, setScreeningName] = useState("");
	const [screeningSaving, setScreeningSaving] = useState(false);
	const headingRef = useRef<HTMLHeadingElement | null>(null);

	/**
	 * A new KPI workflow is created AND started: the user asked for the
	 * numbers, not for a configured-but-empty grid, and the workflow they land
	 * on shows the first run in progress. Navigation happens before the run is
	 * requested so a slow enqueue does not hold them on the modal.
	 */
	const handleKpiSubmit = useCallback(
		async (payload: CreateWorkflowPayload) => {
			const created = await createWorkflow(payload);
			if (!created) return;
			onCreated?.();
			onClose();
			navigate(`/workflows/kpi/${encodeURIComponent(created.id)}`);
			await runNow(created.id);
		},
		[createWorkflow, navigate, onClose, onCreated, runNow],
	);

	/**
	 * Loan tape setup is a page of its own (DOP-627): three steps with the
	 * example the tape copies, which outgrew a modal. Picking the card, or
	 * opening the modal on it, goes there.
	 */
	const openLoanTapeSetup = useCallback(() => {
		onClose();
		navigate(NEW_LOAN_TAPE_PATH);
	}, [navigate, onClose]);

	useEffect(() => {
		if (template === "loan_tape") openLoanTapeSetup();
	}, [template, openLoanTapeSetup]);

	const handleMonitoringSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			// The button is disabled while saving, but a form submits on Enter too.
			if (monitoringSaving) return;
			if (!monitoringName.trim() || !monitoringFund.trim()) return;
			setMonitoringSaving(true);
			try {
				const created = await monitoringService.createTemplate({
					name: monitoringName.trim(),
					fund_name: monitoringFund.trim(),
				});
				onCreated?.();
				onClose();
				navigate(
					`/workflows/monitoring/${encodeURIComponent(created.public_id)}`,
				);
			} catch (caught) {
				// The form stays open with what the user typed still in it: a
				// rejected POST changed nothing, and clearing the fields would make
				// them retype what the server never took.
				showToast(
					caught instanceof MonitoringRequestError
						? friendlyApiErrorMessage(caught.status, caught.message)
						: friendlyErrorMessage(caught, "Could not create the workflow."),
					"error",
				);
			} finally {
				setMonitoringSaving(false);
			}
		},
		[
			monitoringFund,
			monitoringName,
			monitoringSaving,
			navigate,
			onClose,
			onCreated,
			showToast,
		],
	);

	/**
	 * A deal-screening workflow needs only a name: the recipe starts as the
	 * firm's default outline (edited later under Settings), and deals are
	 * added on the workflow page, where the Deal Room folders are listed.
	 */
	const handleScreeningSubmit = useCallback(
		async (event: React.FormEvent) => {
			event.preventDefault();
			if (screeningSaving || !screeningName.trim()) return;
			setScreeningSaving(true);
			try {
				const created = await dealScreeningService.createWorkflow({
					name: screeningName.trim(),
				});
				onCreated?.();
				onClose();
				navigate(
					`/workflows/deal-screening/${encodeURIComponent(created.public_id)}`,
				);
			} catch (caught) {
				// The name stays typed: a rejected POST changed nothing.
				showToast(
					caught instanceof DealScreeningRequestError
						? friendlyApiErrorMessage(caught.status, caught.message)
						: friendlyErrorMessage(caught, "Could not create the workflow."),
					"error",
				);
			} finally {
				setScreeningSaving(false);
			}
		},
		[navigate, onClose, onCreated, screeningName, screeningSaving, showToast],
	);

	const heading =
		template === null ? "New workflow" : "Configure this workflow";
	const subheading =
		template === null
			? "Pick what kind of workflow to create. You name it and add its files next."
			: null;

	return (
		<ModalDialog
			titleId={TITLE_ID}
			size={template === null ? "lg" : "xl"}
			initialFocusRef={headingRef}
			// Step two unmounts whatever had focus; without this it falls to the
			// top of the document, outside the dialog that claims to hold it.
			refocusOn={template ?? "pick"}
			onClose={onClose}
		>
			<div className="flex items-start justify-between gap-3">
				<div>
					<h2
						id={TITLE_ID}
						ref={headingRef}
						tabIndex={-1}
						className="text-xl font-semibold tracking-[-0.01em] text-[#111111] focus:outline-none"
					>
						{heading}
					</h2>
					{subheading && <p className="mt-1 text-sm text-[#5B6472]">{subheading}</p>}
				</div>
				<button
					type="button"
					onClick={onClose}
					className="rounded-lg px-2.5 py-1.5 text-sm font-medium text-[#6B7280] hover:bg-gray-100 hover:text-[#111111]"
				>
					Close
				</button>
			</div>

			{template === null ? (
				<div className="mt-5">
					<div className="grid gap-3 sm:grid-cols-2">
						{kpiExtractionEnabled && (
							<WorkflowTemplateCard
								title="Extract KPIs"
								description="Pull the metrics you pick out of every monthly report and keep the table up to date as new files land."
								onSelect={() => setTemplate("extract_kpis")}
							/>
						)}
						{loanTapeEnabled && (
							<WorkflowTemplateCard
								title="Loan Tape Extraction"
								description="Show Doppler a tape your team already made, and it cleans the next servicer file into the same format."
								icon={FileSpreadsheet}
								badge="Manual"
								onSelect={openLoanTapeSetup}
							/>
						)}
						{monitoringEnabled && (
							<WorkflowTemplateCard
								title="Portfolio Monitoring"
								description="Track every borrower's quarterly package, extract the covenant figures, and export the monitoring workbook."
								icon={ClipboardList}
								badge="Quarterly"
								onSelect={() => setTemplate("monitoring")}
							/>
						)}
						{dealScreeningEnabled && (
							<WorkflowTemplateCard
								title="Opportunity Memo"
								description="Turn a deal folder into a cited opportunity memo: header block, financial summary, strengths and the diligence list. One memo per deal."
								icon={FileText}
								badge="Manual"
								onSelect={() => setTemplate("deal_screening")}
							/>
						)}
					</div>

					{/*
					 * The escape hatch from a fixed menu, present but honest about
					 * what it does today: nothing is wired behind it, so Continue
					 * stays disabled and the card says so rather than accepting a
					 * description and dropping it.
					 */}
					<div className="mt-4 rounded-xl border border-dashed border-gray-300 bg-[#fafafa] p-4">
						<div className="flex items-center gap-2">
							<label htmlFor="new-dashboard-describe" className={TITLE}>
								…or describe what you want to track
							</label>
							<span className={PILL}>Coming soon</span>
						</div>
						<textarea
							id="new-dashboard-describe"
							value={describe}
							onChange={(event) => setDescribe(event.target.value)}
							rows={2}
							placeholder="Every borrower's quarterly leverage against its covenant"
							className={`mt-2 ${INPUT}`}
						/>
						<div className="mt-2.5 flex flex-wrap items-center gap-3">
							<button
								type="button"
								// Never enabled yet: there is no flow behind a description.
								// Drawn as the neutral button so it does not read as clickable.
								disabled
								className={BTN}
							>
								Continue
							</button>
							<p className={HINT}>
								The assistant will confirm scope, cadence and backfill with you
								before anything runs.
							</p>
						</div>
					</div>
				</div>
			) : (
				<div className="mt-4">
					<button
						type="button"
						onClick={() => setTemplate(null)}
						className={`mb-3 ${LINK}`}
					>
						<ArrowLeft className="h-4 w-4" aria-hidden />
						Back to templates
					</button>

					{template === "extract_kpis" && (
						<WorkflowComposer
							metricGroups={metricGroups}
							folders={folders}
							foldersLoading={foldersLoading}
							foldersError={foldersError}
							onRetryFolders={reloadFolders}
							isSaving={isSaving}
							onCancel={onClose}
							onSubmit={handleKpiSubmit}
						/>
					)}


					{template === "monitoring" && (
						<form onSubmit={handleMonitoringSubmit} className={`${CARD} p-5`}>
							<p className={TITLE}>Name your monitoring workflow</p>
							<p className={`mt-1 ${BODY}`}>
								You add its borrowers on the next page.
							</p>
							<div className="mt-4 grid gap-3 sm:grid-cols-2">
								<label className="flex flex-col gap-1">
									<span className={FIELD_LABEL}>Name</span>
									<input
										type="text"
										value={monitoringName}
										onChange={(event) => setMonitoringName(event.target.value)}
										placeholder="Covenant monitoring"
										required
										className={INPUT}
									/>
								</label>
								<label className="flex flex-col gap-1">
									<span className={FIELD_LABEL}>Fund</span>
									<input
										type="text"
										value={monitoringFund}
										onChange={(event) => setMonitoringFund(event.target.value)}
										placeholder="Fund II"
										required
										className={INPUT}
									/>
								</label>
							</div>
							<div className="mt-5 flex items-center gap-2">
								<button
									type="submit"
									disabled={monitoringSaving}
									className={BTN_PRIMARY}
								>
									{monitoringSaving ? "Creating…" : "Create workflow"}
								</button>
								<button
									type="button"
									onClick={onClose}
									className={BTN}
								>
									Cancel
								</button>
							</div>
						</form>
					)}
					{template === "deal_screening" && (
						<form onSubmit={handleScreeningSubmit} className={`${CARD} p-5`}>
							<p className={TITLE}>Name your opportunity memo workflow</p>
							<p className={`mt-1 ${BODY}`}>
								Each deal is a Deal Room folder; you add them on the next page.
							</p>
							<label className="mt-4 flex flex-col gap-1 sm:max-w-md">
								<span className={FIELD_LABEL}>Name</span>
								<input
									type="text"
									value={screeningName}
									onChange={(event) => setScreeningName(event.target.value)}
									placeholder="Opportunity memo"
									required
									className={INPUT}
								/>
							</label>
							<div className="mt-5 flex items-center gap-2">
								<button
									type="submit"
									disabled={screeningSaving}
									className={BTN_PRIMARY}
								>
									{screeningSaving ? "Creating…" : "Create workflow"}
								</button>
								<button
									type="button"
									onClick={onClose}
									className={BTN}
								>
									Cancel
								</button>
							</div>
						</form>
					)}
				</div>
			)}
		</ModalDialog>
	);
};

export default NewDashboardModal;
